<template>
  <div class="ai-audience-form">
    <NlFilterBar :current-filters="filters" @filters-received="applyAiFilters" @generating-start="loading = true" @generating-abort="loading = false" />
    <div v-if="aiIgnored.length" class="not-enforced-note" style="margin: -6px 0 12px">
      <div v-for="(text, i) in aiIgnored" :key="i">{{ text }}</div>
    </div>

    <div class="cust-bar">
      <div class="cust-field">
        <span class="cust-label required">Customer</span>
        <treeselect
          class="cust-select"
          :clearable="false"
          placeholder="--Choose a Customer--"
          :options="customerOptions"
          v-model="filters.customer"
          @input="handleCustomerInput" />
      </div>
      <div class="cust-field">
        <span class="cust-label">Country</span>
        <treeselect
          class="cust-select"
          placeholder="--Choose a Country--"
          :options="countryOptions"
          v-model="filters.country"
          @input="handleCountryInput" />
      </div>
      <div class="cust-meta">
        <span class="cust-context-pill" v-if="filters.customer && filters.country">{{ filters.customer }} · {{ filters.country }}</span>
        <span class="cust-hint">Customer and country filter each other</span>
        <span v-if="submitted && !filters.customer" class="text-red">Please choose a customer.</span>
        <span v-else-if="submitted && !filters.country_id" class="text-red">Please choose a country.</span>
      </div>
    </div>

    <div class="builder-layout">
      <div class="builder-left">
        <div class="filter-panel">
          <div class="fp-hdr">
            <div class="fp-title">
              Audience Filters
              <span class="ai-badge" :class="aiPopulatedCount ? 'ai-badge-ai' : 'ai-badge-manual'">
                {{ aiPopulatedCount ? `${aiPopulatedCount} field(s) AI populated` : 'Manual' }}
              </span>
            </div>
            <div class="fp-hdr-actions">
              <a href="#" class="fp-action" @click.prevent="setAllSections(false)">Expand all</a>
              <a href="#" class="fp-action" @click.prevent="setAllSections(true)">Collapse all</a>
              <a href="#" class="fp-action fp-action-danger" @click.prevent="clearFilters">Clear all</a>
            </div>
          </div>

          <div class="fp-body">
            <!-- Programme -->
            <section class="filter-section" :class="{ collapsed: collapsed.programme, 'ai-updated': aiFlash.programme }">
              <div class="fs-header" @click="toggleSection('programme')">
                <span class="fs-title-text">Programme</span>
                <span class="fs-arrow">▾</span>
              </div>
              <div class="fs-body" v-show="!collapsed.programme">
                <div class="filter-row cols-2">
                  <div class="fg">
                    <div class="fg-lbl">Journey <span class="req">*</span></div>
                    <treeselect
                      :clearable="false"
                      placeholder="--Choose a Journey--"
                      :options="journeyOptions"
                      :disabled="!filters.customer || !filters.country"
                      v-model="filters.journey"
                      @input="handleJourneyInput" />
                    <span v-if="submitted && !filters.journey" class="text-red">Please choose a journey.</span>
                  </div>
                  <div class="fg">
                    <div class="fg-lbl">Platform <span class="req">*</span></div>
                    <div class="chip-wrap">
                      <div
                        v-for="channel in channelOptions"
                        :key="channel.value"
                        class="fchip"
                        :class="{ sel: filters.channels.includes(channel.value) }"
                        @click="toggleChip('channels', channel.value)">
                        <span class="chk"></span>{{ channel.label }}
                      </div>
                    </div>
                    <span v-if="submitted && !filters.channels.length" class="text-red">Please choose at least one platform.</span>
                  </div>
                </div>

                <div class="fg" style="margin-top: 4px">
                  <div class="fg-lbl">Funnel Stage <span class="fg-sub">(select the range of stages to include)</span></div>
                  <div class="funnel-range-row">
                    <div class="funnel-range-col">
                      <div class="funnel-range-label">From (earliest stage reached)</div>
                      <nv-select class="form-select" v-model="filters.funnelFrom" @change="handleFunnelFromInput">
                        <option value="">Reached chatbot</option>
                        <option v-for="stage in funnelStageOptions.slice(1)" :key="stage.value" :value="stage.value">{{ stage.label }}</option>
                      </nv-select>
                    </div>
                    <div class="funnel-range-sep">→</div>
                    <div class="funnel-range-col">
                      <div class="funnel-range-label">To (stage not reached)</div>
                      <nv-select class="form-select" v-model="filters.funnelTo" @change="markManual('funnelTo'); maybeGenerateName()">
                        <option value="any">Any — no upper limit</option>
                        <option v-for="stage in funnelToOptions" :key="stage.value" :value="stage.value">{{ stage.label }}</option>
                      </nv-select>
                    </div>
                  </div>
                  <div class="funnel-hint" v-html="funnelHint"></div>
                  <span v-if="funnelRangeWarning" class="text-red">"To" stage must be later than "From" stage.</span>
                  <span v-if="filters.journey && !journeyStageMapping.length" class="not-enforced-note">
                    This journey has no stage mapping configured in Journey Settings yet — this range won't be applied to the count until it is.
                  </span>
                </div>

                <div class="fg" style="margin-top: 12px">
                  <div class="fg-lbl">Date entered <span class="funnel-from-label">{{ funnelFromLabel }}</span> <span class="fg-sub">(optional)</span></div>
                  <div class="date-range-row">
                    <input type="date" class="form-input" v-model="filters.stageEnteredFrom" @change="markManual('stageEnteredFrom')" />
                    <span class="range-sep">to</span>
                    <input type="date" class="form-input" v-model="filters.stageEnteredTo" @change="markManual('stageEnteredTo')" />
                    <a href="#" class="fp-action" @click.prevent="filters.stageEnteredFrom = ''; filters.stageEnteredTo = ''; markManual('stageEnteredFrom'); markManual('stageEnteredTo')">Clear</a>
                  </div>
                  <span v-if="stageEnteredWarning" class="text-red">"From" date must be before "To" date.</span>
                </div>

                <div class="filter-row cols-2" style="margin-top: 14px; margin-bottom: 0">
                  <div class="fg">
                    <div class="fg-lbl">Conversation Stage <span class="fg-sub">(one specific stage in this journey's user reached to)</span></div>
                    <treeselect
                      placeholder="--Choose a stage--"
                      :options="conversationStageOptionsForJourney"
                      :disabled="!filters.journey"
                      :loading="loadingJourneyConversations"
                      v-model="filters.conversationStageOption"
                      @input="handleConversationStageInput" />
                    <span v-if="filters.journey && !conversationStageOptionsForJourney.length && !loadingJourneyConversations" class="not-enforced-note">
                      Couldn't load this journey's conversation stages.
                    </span>
                  </div>
                </div>
              </div>
            </section>

            <!-- Demographics -->
            <section class="filter-section" :class="{ collapsed: collapsed.demographics, 'ai-updated': aiFlash.demographics }">
              <div class="fs-header" @click="toggleSection('demographics')">
                <span class="fs-title-text">Demographics</span>
                <span class="fs-arrow">▾</span>
              </div>
              <div class="fs-body" v-show="!collapsed.demographics">
                <div class="filter-row cols-2">
                  <div class="fg">
                    <div class="fg-lbl">Age range</div>
                    <div class="date-range-row">
                      <nv-select class="form-select" v-model="filters.minAge" @change="markManual('minAge')">
                        <option v-for="age in ageOptions" :key="'min-' + age.id" :value="age.id">{{ age.label }}</option>
                      </nv-select>
                      <span class="range-sep">to</span>
                      <nv-select class="form-select" v-model="filters.maxAge" @change="markManual('maxAge')">
                        <option v-for="age in ageOptions" :key="'max-' + age.id" :value="age.id">{{ age.label }}</option>
                      </nv-select>
                    </div>
                    <span v-if="ageWarning" class="text-red">"Age From" must be less than "Age To".</span>
                  </div>
                  <div class="fg">
                    <div class="fg-lbl">Gender</div>
                    <div class="chip-wrap">
                      <div
                        v-for="gender in genderOptions"
                        :key="gender.value"
                        class="fchip"
                        :class="{ sel: filters.gender.includes(gender.value) }"
                        @click="toggleChip('gender', gender.value)">
                        <span class="chk"></span>{{ gender.label }}
                      </div>
                      <div class="fchip" :class="{ sel: !filters.gender.length }" @click="filters.gender = []; markManual('gender')">
                        <span class="chk"></span>All
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <!-- Geography -->
            <section class="filter-section" :class="{ collapsed: collapsed.geography, 'ai-updated': aiFlash.geography }">
              <div class="fs-header" @click="toggleSection('geography')">
                <span class="fs-title-text">Geography</span>
                <span class="fs-arrow">▾</span>
              </div>
              <div class="fs-body" v-show="!collapsed.geography">
                <div class="filter-row cols-2">
                  <div class="fg">
                    <div class="fg-lbl">State / Province <span class="fg-sub">{{ filters.country || '' }}</span></div>
                    <treeselect
                      placeholder="All"
                      :options="stateOptions"
                      :disabled="!filters.country"
                      v-model="filters.stateProvince"
                      @input="handleStateInput" />
                  </div>
                  <div class="fg">
                    <div class="fg-lbl">City / LGA</div>
                    <treeselect
                      placeholder="All"
                      :options="cityOptions"
                      :disabled="!filters.stateProvince"
                      v-model="filters.cityLga"
                      @input="markManual('cityLga')" />
                    <div class="fg-sub-note">Populated by selected state</div>
                  </div>
                </div>
                <span v-if="filters.country && !stateOptions.length" class="not-enforced-note">
                  No state/province with sampled users was found for this country.
                </span>
              </div>
            </section>

            <!-- Behaviour -->
            <section class="filter-section" :class="{ collapsed: collapsed.behaviour, 'ai-updated': aiFlash.behaviour }">
              <div class="fs-header" @click="toggleSection('behaviour')">
                <span class="fs-title-text">Behaviour</span>
                <span class="fs-arrow">▾</span>
              </div>
              <div class="fs-body" v-show="!collapsed.behaviour">
                <div class="fg" style="margin-bottom: 12px">
                  <div class="fg-lbl">Inactivity window</div>
                  <div class="date-range-row">
                    <nv-select class="form-select" v-model="filters.recencyFrom" @change="markManual('recencyFrom')">
                      <option v-for="r in recencyFromOptions" :key="'from-' + r.id" :value="r.id">{{ r.label }}</option>
                    </nv-select>
                    <span class="range-sep">to</span>
                    <nv-select class="form-select" v-model="filters.recencyTo" @change="markManual('recencyTo')">
                      <option v-for="r in recencyOptions" :key="'to-' + r.id" :value="r.id">{{ r.label }}</option>
                    </nv-select>
                    <span class="range-sep">inactive</span>
                  </div>
                  <span v-if="recencyWarning" class="text-red">"Inactive From" must be less than "Inactive To".</span>
                </div>
                <div class="barriers-row">
                  <span>⚠</span>
                  <span style="flex: 1">Barrier filters unavailable — no data collected for this journey yet.</span>
                  <router-link to="/user-journey/settings">Enable in Journey Settings ›</router-link>
                </div>
              </div>
            </section>

            <!-- Entry Keywords -->
            <section class="filter-section" :class="{ collapsed: collapsed.keywords, 'ai-updated': aiFlash.keywords }">
              <div class="fs-header" @click="toggleSection('keywords')">
                <span class="fs-title-text">Entry Keywords</span>
                <span class="fs-arrow">▾</span>
              </div>
              <div class="fs-body" v-show="!collapsed.keywords">
                <div class="fg">
                  <div class="fg-lbl">Entry keyword(s) <span class="fg-sub">(users who entered via any selected keyword)</span></div>
                  <treeselect
                    :key="'keywords-' + (filters.country || 'none')"
                    :multiple="true"
                    :flat="true"
                    :limit="6"
                    :limit-text="count => `+${count} more`"
                    :max-height="260"
                    :options="keywordTreeOptions"
                    :disabled="!filters.country"
                    :placeholder="filters.country ? `Search and select from ${keywordOptions.length} keywords…` : 'Choose a customer/country to load keywords'"
                    :no-options-text="'No keywords found for this country.'"
                    v-model="filters.entryKeywords"
                    @input="markManual('entryKeywords')" />
                  <div class="fg-sub-note">Keywords are scoped to the selected country. Users who entered via any selected keyword are included.</div>
                </div>
              </div>
            </section>

            <!-- Readiness — only shown if this journey has a Readiness stage configured in Journey Settings -->
            <section v-if="readinessStageOptions.length" class="filter-section" :class="{ collapsed: collapsed.readiness, 'ai-updated': aiFlash.readiness }">
              <div class="fs-header" @click="toggleSection('readiness')">
                <span class="fs-title-text">Readiness</span>
                <span class="fs-arrow">▾</span>
              </div>
              <div class="fs-body" v-show="!collapsed.readiness">
                <div class="fg">
                  <div class="fg-lbl">Readiness level <span class="fg-sub">(real answer options detected for this journey's Readiness stage)</span></div>
                  <div class="chip-wrap">
                    <div
                      v-for="opt in readinessStageOptions"
                      :key="opt"
                      class="fchip"
                      :class="{ sel: filters.readiness.includes(opt) }"
                      @click="toggleChip('readiness', opt)">
                      <span class="chk"></span>{{ opt }}
                    </div>
                  </div>
                  <div class="not-enforced-note">Not yet enforced in the live count — no data model links readiness responses back to users today; captured on the saved segment regardless.</div>
                </div>
              </div>
            </section>

            <!-- Proximity to Facility -->
            <section class="filter-section" :class="{ collapsed: collapsed.proximity, 'ai-updated': aiFlash.proximity }">
              <div class="fs-header" @click="toggleSection('proximity')">
                <span class="fs-title-text">Proximity to Facility</span>
                <span class="fs-arrow">▾</span>
              </div>
              <div class="fs-body" v-show="!collapsed.proximity">
                <div class="fg" style="margin-bottom: 12px">
                  <div class="fg-lbl">Offerings <span class="fg-sub">(services/products a facility must provide)</span></div>
                  <treeselect
                    style="margin-bottom: 8px"
                    placeholder="All offerings"
                    :multiple="true"
                    :options="offeringOptions"
                    :disabled="!filters.country"
                    v-model="filters.offeringIds"
                    @input="onOfferingsChange" />
                </div>
                <div class="fg" style="margin-bottom: 12px">
                  <div class="fg-lbl">Facility <span class="fg-sub">(type to search — select one or more facilities in the selected country)</span></div>
                  <div class="date-range-row" style="margin-bottom: 8px">
                    <label v-if="filters.customer" style="display: flex; align-items: center; gap: 6px; font-size: 12px; color: #616161; cursor: pointer; white-space: nowrap">
                      <input type="checkbox" v-model="filters.facilityScopeToCustomer" @change="onFacilityScopeChange" />
                      Limit to {{ filters.customer }}'s facilities
                    </label>
                  </div>
                  <treeselect
                    :key="'facility-' + (filters.country || 'none') + '-' + (filters.offeringIds || []).join('_') + '-' + filters.facilityScopeToCustomer"
                    placeholder="Search facilities…"
                    :multiple="true"
                    :options="facilityOptions"
                    :async="true"
                    :load-options="loadFacilityOptions"
                    :disabled="!filters.country"
                    v-model="filters.facilityIds"
                    @input="markManual('facilityIds')" />
                </div>
                <div class="fg">
                  <div class="fg-lbl" :class="{ required: hasProximityConstraint }">Radius</div>
                  <div class="date-range-row">
                    <nv-select class="form-select" style="max-width: 170px" v-model="filters.radiusKm" @change="markManual('radiusKm')">
                      <option v-for="r in radiusOptions" :key="'radius-' + r.id" :value="r.id">{{ r.label }}</option>
                    </nv-select>
                    <span class="range-sep">of matching facilities</span>
                  </div>
                  <span v-if="submitted && proximityRadiusWarning" class="text-red">Select a radius — it's required once a facility, offering, or client filter is set.</span>
                  <div class="warn-note">
                    <span>🧭</span>
                    <span>Matches on the user's referral-capture location or their general location — whichever is within range. Users with neither stored are excluded from the radius match.</span>
                  </div>
                </div>
              </div>
            </section>

            <!-- Exclusions -->
            <section class="filter-section" :class="{ collapsed: collapsed.exclusions, 'ai-updated': aiFlash.exclusions }">
              <div class="fs-header" @click="toggleSection('exclusions')">
                <span class="fs-title-text">Exclusions</span>
                <span class="fs-arrow">▾</span>
              </div>
              <div class="fs-body" v-show="!collapsed.exclusions">
                <div class="fg" style="margin-bottom: 12px">
                  <div class="fg-lbl">Exclude users already in these segments</div>
                  <div class="exclusion-checklist">
                    <div v-if="!otherSegments.length" class="text-muted">No other saved segments yet.</div>
                    <div v-for="segment in otherSegments" :key="segment.id" class="exclusion-row">
                      <label>
                        <input type="checkbox" :value="segment.id" v-model="filters.excludeSegmentIds" />
                        {{ segment.queryName }}
                        <span class="exclusion-count">({{ segment.criteria && segment.criteria.savedUserCount !== undefined ? segment.criteria.savedUserCount : '—' }} users)</span>
                      </label>
                    </div>
                  </div>
                  <span v-if="filters.excludeSegmentIds.length" class="not-enforced-note">
                    These segments are also excluded again at campaign send time.
                  </span>
                </div>
                <div class="fg">
                  <div class="fg-lbl">Exclude users currently in an active conversation</div>
                  <label class="active-conv-toggle" :class="{ 'active-conv-toggle-on': filters.activeConversationExclude }" @click="toggleActiveConversationExclude">
                    <span class="active-conv-check">{{ filters.activeConversationExclude ? '✓' : '' }}</span>
                    <span>Exclude users who are currently in an active session for the selected journey</span>
                  </label>
                  <div class="fg-sub-note">Prevents users from being nudged while already engaged in a conversation. Recommended for all re-engagement campaigns.</div>
                </div>
                <div class="fg-sub-note" style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #f0f0f0">
                  Frequency cap, cooldown, and STOP opt-outs are always applied automatically before a campaign sends.
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <div class="builder-right">
        <div class="aud-card">
          <div class="aud-count-box">
            <div class="aud-num">{{ (audienceCount || 0).toLocaleString() }}</div>
            <div>
              <div class="aud-lbl">users match<br />your criteria</div>
              <div v-if="countStale" class="not-enforced-note" style="margin-top: 4px">Filters changed since this count — click Get Audience to refresh.</div>
              <div style="margin-top: 6px"><span class="pill pill-green">✓ Guardrails applied</span></div>
            </div>
          </div>
          <div class="aud-note">
            Frequency cap, 48-hour cooldown, and STOP opt-outs are removed automatically before a campaign sends — the
            exact per-category breakdown isn't exposed by the counting service used by this screen.
          </div>
          <button type="button" class="btn btn-outline btn-block" @click="getAudience">↻ Refresh count</button>
        </div>

        <div class="save-card">
          <div class="save-card-title">Save as segment</div>
          <input type="text" class="seg-name-input" v-model="segmentName" @input="nameManuallyEdited = true" placeholder="Segment name" />
          <span class="seg-name-auto">Auto-generated · editable</span>
          <span v-if="submitted && !segmentName.trim()" class="text-red" style="display: block; margin-bottom: 8px">Please enter a segment name.</span>
          <button type="button" class="btn btn-pink btn-block" @click="saveSegment">{{ filters.id ? 'Update' : 'Save' }} Segment ›</button>
        </div>
      </div>
    </div>

    <BlockUI v-if="loading"><i class="fa fa-cog fa-spin fa-3x fa-fw"></i><br />Please wait...</BlockUI>
    <AudienceTable :audienceData="audienceData" :audienceCount="audienceCount" :showTable="true" :hasRun="countRun" />
  </div>
</template>

<script>
import moment from 'moment'
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'
import api from '../../../../utils/latestApi'
import apiold from '../../../../utils/api'
import { getEnv } from '../../../../utils/utilities'
import { channelOptions, genderOptions } from '../../../../config/constants'
import AudienceTable from '../table'
import * as listHelpers from '../listQueries/helpers'
import NlFilterBar from './NlFilterBar.vue'
import {
  funnelStageOptions,
  ageOptions,
  recencyOptions,
  barrierOptions,
  radiusOptions
} from './constants'
import {
  defaultFilters,
  validateFilters,
  buildCountPayload,
  buildSaveCriteria,
  criteriaToFilters,
  generateSegmentName,
  funnelStageValues,
  funnelRangeToStages,
  funnelRangeHint
} from './helpers'

// Maps a filter field to the collapsible section it lives in, so the AI-populate flash
// highlights the right section after an NL filter pass.
const SECTION_FOR_FIELD = {
  customer: 'programme',
  country: 'programme',
  journey: 'programme',
  channels: 'programme',
  funnelFrom: 'programme',
  funnelTo: 'programme',
  conversationStageOption: 'programme',
  minAge: 'demographics',
  maxAge: 'demographics',
  gender: 'demographics',
  stateProvince: 'geography',
  cityLga: 'geography',
  recencyFrom: 'behaviour',
  recencyTo: 'behaviour',
  stageEnteredFrom: 'programme',
  stageEnteredTo: 'programme',
  barriers: 'behaviour',
  entryKeywords: 'keywords',
  readiness: 'readiness',
  activeConversationExclude: 'exclusions'
}

export default {
  name: 'ai-audience-form',
  components: {
    AudienceTable,
    NlFilterBar,
    treeselect: Treeselect
  },
  data () {
    return {
      loading: false,
      submitted: false,
      env: '',
      filters: defaultFilters(),
      aiPopulated: {},
      collapsed: {
        programme: false,
        demographics: false,
        geography: false,
        behaviour: false,
        keywords: true,
        readiness: true,
        proximity: true,
        exclusions: false
      },
      aiFlash: {},
      countryIdMap: {},
      customerOptions: [],
      countryChoicesForCustomer: [],
      allCountries: [],
      customersRequestedFor: null,
      customerReqId: 0,
      appliedCustomer: null,
      appliedCountry: null,
      journeyOptions: [],
      stateOptions: [],
      cityOptions: [],
      keywordOptions: [],
      keywordSearch: '',
      journeyStageMapping: [],
      journeyConversations: [],
      loadingJourneyConversations: false,
      facilitySearchTimer: null,
      facilityOptions: [],
      offeringOptions: [],
      existingSegments: [],
      segmentName: '',
      nameManuallyEdited: false,
      audienceCount: null,
      countRun: false,
      countStale: false,
      aiIgnored: [],
      keywordsCountry: null,
      audienceData: [],
      channelOptions,
      genderOptions,
      funnelStageOptions,
      ageOptions,
      recencyOptions,
      radiusOptions
    }
  },
  computed: {
    aiPopulatedCount () {
      return Object.values(this.aiPopulated).filter(Boolean).length
    },
    funnelStagesComputed () {
      return funnelRangeToStages(this.filters.funnelFrom, this.filters.funnelTo)
    },
    funnelToOptions () {
      // "To" is the stage NOT reached, so only stages strictly after "From" are valid choices.
      const fromIdx = this.filters.funnelFrom === '' ? 0 : funnelStageValues.indexOf(this.filters.funnelFrom)
      return this.funnelStageOptions.filter((stage, idx) => idx > fromIdx)
    },
    funnelFromLabel () {
      const fromIdx = this.filters.funnelFrom === '' ? 0 : funnelStageValues.indexOf(this.filters.funnelFrom)
      return (this.funnelStageOptions[fromIdx] || this.funnelStageOptions[0]).label
    },
    funnelHint () {
      return funnelRangeHint(this.filters.funnelFrom, this.filters.funnelTo)
    },
    // Conversation Stage is just "one specific stage of this journey" — sourced directly from the
    // journey's own conversation config (same getJourneyConversationsList data Journey Settings uses
    // for its stage mapping), not a fixed hardcoded list and not a funnel-stage business label. Value
    // is encoded as "conversation::stage" since a journey can embed more than one conversation and
    // the same stage key could exist in more than one of them.
    // 'All time' only makes sense as the upper bound of the inactivity window.
    recencyFromOptions () {
      return recencyOptions.filter(r => r.id !== 'all_time')
    },
    // Reactive both ways: with a customer picked, only its countries; otherwise every country.
    countryOptions () {
      const names = this.filters.customer ? this.countryChoicesForCustomer : this.allCountries
      return names.map(c => ({ id: c, label: c }))
    },
    conversationStageOptionsForJourney () {
      const multipleConversations = this.journeyConversations.length > 1
      const options = []
      this.journeyConversations.forEach(convo => {
        (convo.stages || []).forEach(stage => {
          options.push({
            id: `${convo.name}::${stage}`,
            label: multipleConversations ? `${stage} (${convo.name})` : stage
          })
        })
      })
      return options
    },
    // Readiness only shows once this journey has a stage keyed 'readiness' configured in Journey
    // Settings — its chip options are the real answer options auto-detected there (read-only), not
    // a fixed enum.
    readinessStageOptions () {
      const stage = this.journeyStageMapping.find(s => s.key === 'readiness')
      return (stage && stage.options) || []
    },
    keywordTreeOptions () {
      return this.keywordOptions.map(kw => ({ id: kw, label: kw }))
    },
    ageWarning () {
      return !validateFilters(this.filters).ageValid
    },
    recencyWarning () {
      return !validateFilters(this.filters).recencyValid
    },
    funnelRangeWarning () {
      return !validateFilters(this.filters).funnelRangeValid
    },
    stageEnteredWarning () {
      return !validateFilters(this.filters).stageEnteredValid
    },
    proximityRadiusWarning () {
      return !validateFilters(this.filters).proximityRadiusValid
    },
    hasProximityConstraint () {
      return (this.filters.facilityIds && this.filters.facilityIds.length) ||
        (this.filters.offeringIds && this.filters.offeringIds.length) ||
        this.filters.facilityScopeToCustomer
    },
    otherSegments () {
      return this.existingSegments.filter(s => s.id !== this.filters.id)
    }
  },
  async mounted () {
    this.loading = true
    this.env = await getEnv()
    await Promise.all([
      this.fetchCountryIdMap(),
      this.fetchAllCustomerOptions(),
      this.fetchAllCountries(),
      this.fetchExistingSegments()
    ])
    if (this.$route.query.queryId) {
      await this.loadSegmentForEdit(this.$route.query.queryId)
    }
    this.loading = false
    this.emitContext()
  },
  watch: {
    filters: {
      deep: true,
      handler () { if (this.countRun) this.countStale = true }
    },
    'filters.customer' () { this.emitContext() },
    'filters.country' () { this.emitContext() }
  },
  methods: {
    emitContext () {
      this.$emit('context-changed', { customer: this.filters.customer, country: this.filters.country })
    },
    toggleSection (key) {
      this.$set(this.collapsed, key, !this.collapsed[key])
    },
    setAllSections (isCollapsed) {
      Object.keys(this.collapsed).forEach(key => { this.$set(this.collapsed, key, isCollapsed) })
    },
    toggleChip (key, value) {
      const arr = this.filters[key]
      const idx = arr.indexOf(value)
      if (idx === -1) arr.push(value)
      else arr.splice(idx, 1)
      this.markManual(key)
      this.maybeGenerateName()
    },
    toggleActiveConversationExclude () {
      this.filters.activeConversationExclude = !this.filters.activeConversationExclude
      this.markManual('activeConversationExclude')
    },
    markManualMany (keys) {
      keys.forEach(k => this.markManual(k))
    },
    markManual (key) {
      this.$set(this.aiPopulated, key, false)
    },
    markAi (key) {
      this.$set(this.aiPopulated, key, true)
      this.flashSection(SECTION_FOR_FIELD[key])
    },
    flashSection (key) {
      if (!key) return
      if (this.collapsed[key]) this.$set(this.collapsed, key, false)
      this.$set(this.aiFlash, key, false)
      this.$nextTick(() => {
        this.$set(this.aiFlash, key, true)
        setTimeout(() => this.$set(this.aiFlash, key, false), 4000)
      })
    },
    async fetchCountryIdMap () {
      try {
        const response = await apiold.buildRequest('/getCountryList', 'POST')
        const map = {}
        ;(response.data.payload || []).forEach(item => { map[item.country_name] = item.country_id })
        this.countryIdMap = map
      } catch (error) {
        this.$swal({ type: 'error', text: 'Failed to load country list' })
      }
    },
    async fetchAllCustomerOptions (country) {
      // Latest request wins: a slower, older response must not overwrite the list the user now needs.
      const reqId = ++this.customerReqId
      this.customersRequestedFor = country || null
      try {
        const response = await apiold.buildRequest('/audience-builder/journey-map/customers', 'GET', country ? { country } : {})
        if (reqId !== this.customerReqId) return
        this.customerOptions = (response.data.payload || []).map(name => ({ id: name, label: name }))
      } catch (error) {
        if (reqId !== this.customerReqId) return
        this.customersRequestedFor = null
        this.$swal({ type: 'error', text: 'Failed to load customers' })
      }
    },
    async fetchAllCountries () {
      try {
        const response = await apiold.buildRequest('/audience-builder/journey-map/countries', 'GET')
        this.allCountries = response.data.payload || []
      } catch (error) {
        this.allCountries = []
      }
    },
    async fetchJourneyOptionsFor (country, customer) {
      if (!country || !customer) {
        this.journeyOptions = []
        return
      }
      try {
        const response = await apiold.buildRequest('/audience-builder/journey-map/journeys', 'GET', { country, customer })
        // Drop the response if the country/customer changed while it was in flight.
        if (country !== this.filters.country || customer !== this.filters.customer) return
        this.journeyOptions = (response.data.payload || []).map(name => ({ id: name, label: name }))
      } catch (error) {
        if (country === this.filters.country && customer === this.filters.customer) this.journeyOptions = []
      }
    },
    async fetchStateOptionsFor (country) {
      if (!country) {
        this.stateOptions = []
        return
      }
      try {
        const response = await apiold.buildRequest('/audience-builder/journey-map/states', 'GET', { country })
        if (country !== this.filters.country) return
        this.stateOptions = (response.data.payload || []).map(row => ({ id: row.state, label: `${row.state} (${row.count})` }))
      } catch (error) {
        if (country === this.filters.country) this.stateOptions = []
      }
    },
    async fetchCityOptionsFor (country, state) {
      if (!country || !state) {
        this.cityOptions = []
        return
      }
      try {
        const response = await apiold.buildRequest('/audience-builder/journey-map/cities', 'GET', { country, state })
        this.cityOptions = (response.data.payload || []).map(row => ({ id: row.city, label: `${row.city} (${row.count})` }))
      } catch (error) {
        this.cityOptions = []
      }
    },
    async fetchOfferingOptionsFor (country) {
      if (!country) {
        this.offeringOptions = []
        return
      }
      try {
        const response = await apiold.buildRequest('/audience-builder/journey-map/offerings', 'GET', { country })
        if (country !== this.filters.country) return
        this.offeringOptions = (response.data.payload || []).map(row => ({
          id: row.id,
          label: row.type ? `${row.name} (${row.type})` : row.name
        }))
      } catch (error) {
        if (country === this.filters.country) this.offeringOptions = []
      }
    },
    // Changing the offerings filter re-scopes the facility list, so the current facility pick may no
    // longer qualify — clear it and let the (re-keyed) async treeselect reload.
    onOfferingsChange () {
      this.filters.facilityIds = []
      this.markManual('offeringIds')
    },
    // Toggling client scoping re-scopes the facility list the same way; clear the current pick so a
    // now-out-of-scope facility isn't left selected.
    onFacilityScopeChange () {
      this.filters.facilityIds = []
      this.markManual('facilityScopeToCustomer')
    },
    async fetchKeywordOptionsFor (country) {
      const countryId = this.countryIdMap[country]
      if (!countryId) {
        this.keywordOptions = []
        return
      }
      try {
        const response = await apiold.buildRequest('/getAllKeywords', 'POST', { countryId })
        if (country !== this.filters.country) return
        this.keywordOptions = (response.data.payload || []).map(item => item.keyword)
      } catch (error) {
        if (country === this.filters.country) this.keywordOptions = []
      }
    },
    // A saved segment's selected facilities may not be among the default top-50 alphabetical results,
    // which would otherwise render as "(unknown)" in the treeselect — fetch each selected row by id
    // directly so the multi-select shows their labels.
    async hydrateSavedFacility (country, facilityIds) {
      const ids = (facilityIds || []).filter(Boolean)
      if (!ids.length) {
        this.facilityOptions = []
        return
      }
      try {
        const results = await Promise.all(ids.map(id =>
          apiold.buildRequest('/audience-builder/journey-map/facilities', 'GET', { country, id })
            .then(response => (response.data.payload || []).map(row => ({ id: row.id, label: row.name })))
            .catch(() => [])
        ))
        this.facilityOptions = results.flat()
      } catch (error) {
        this.facilityOptions = []
      }
    },
    // vue-treeselect async loadOptions — server always caps results (see facilityData.js), and this
    // is debounced client-side too since some countries have thousands of referral_resources rows and
    // loading them all unbounded previously froze the dropdown.
    loadFacilityOptions ({ searchQuery, callback }) {
      if (!this.filters.country) {
        this.facilityOptions = []
        callback(null, [])
        return
      }
      clearTimeout(this.facilitySearchTimer)
      this.facilitySearchTimer = setTimeout(async () => {
        try {
          const response = await apiold.buildRequest('/audience-builder/journey-map/facilities', 'GET', {
            country: this.filters.country,
            search: searchQuery,
            customer: this.filters.facilityScopeToCustomer ? this.filters.customer : undefined,
            offeringIds: (this.filters.offeringIds && this.filters.offeringIds.length) ? this.filters.offeringIds.join(',') : undefined
          })
          // vue-treeselect async mode renders ONLY what's passed to the callback — the :options prop
          // is not used for search results. Setting this.facilityOptions alone left the dropdown empty
          // even though the API returned rows; the options must be delivered via callback(null, options).
          const options = (response.data.payload || []).map(row => ({ id: row.id, label: row.name }))
          this.facilityOptions = options
          callback(null, options)
        } catch (error) {
          callback(new Error('Failed to load facilities'))
        }
      }, 300)
    },
    async fetchExistingSegments () {
      try {
        const response = await api.get(`audience-builder/${this.env.env}/queries`, { baseURL: this.env.baseURL })
        this.existingSegments = listHelpers.formatQueriesRawData(response.data.data)
      } catch (error) {
        // segment library / exclusion list is best-effort — don't block the form on this
      }
    },
    async handleCustomerInput (customerName) {
      // vue-treeselect re-emits `input` when the value is changed from code (AI apply, loading a saved
      // segment). Ignore that echo so it can't wipe the values just applied.
      if (customerName === this.appliedCustomer) return
      this.appliedCustomer = customerName || null
      this.markManual('customer')
      // Customer list was narrowed by a country pick: restore it so the user can still switch customer.
      if (this.customersRequestedFor) this.fetchAllCustomerOptions()
      const previousCountry = this.filters.country
      this.appliedCountry = null
      this.markManualMany(['country', 'journey', 'conversationStageOption', 'readiness', 'stateProvince', 'cityLga', 'facilityIds'])
      this.filters.country = null
      this.filters.country_id = null
      this.resetJourneyState()
      this.filters.stateProvince = null
      this.filters.cityLga = null
      this.filters.facilityIds = []
      this.stateOptions = []
      this.cityOptions = []
      this.keywordOptions = []
      this.facilityOptions = []
      this.countryChoicesForCustomer = []
      if (!customerName) return
      try {
        const response = await apiold.buildRequest('/audience-builder/journey-map/countries-for-customer', 'GET', { customer: customerName })
        // A newer customer pick superseded this response.
        if (customerName !== this.appliedCustomer) return
        this.countryChoicesForCustomer = response.data.payload || []
      } catch (error) {
        if (customerName !== this.appliedCustomer) return
        this.countryChoicesForCustomer = []
      }
      if (previousCountry && this.countryChoicesForCustomer.includes(previousCountry)) {
        await this.selectCountry(previousCountry)
      } else if (this.countryChoicesForCustomer.length === 1) {
        await this.selectCountry(this.countryChoicesForCustomer[0])
      }
    },
    // Country picked (or cleared) by the user. Narrows the customer list to that country; clearing
    // restores the full list. A customer that doesn't operate in the new country can't be chosen
    // because countryOptions is already limited to the customer's countries.
    async handleCountryInput (countryName) {
      // Ignore the echo treeselect emits for programmatic changes (see handleCustomerInput).
      if ((countryName || null) === this.appliedCountry) return
      if (!countryName) {
        this.appliedCountry = null
        this.keywordsCountry = null
        this.filters.entryKeywords = []
        this.keywordSearch = ''
        this.markManualMany(['country', 'journey', 'conversationStageOption', 'readiness', 'stateProvince', 'cityLga', 'facilityIds', 'entryKeywords'])
        this.filters.country = null
        this.filters.country_id = null
        this.resetJourneyState()
        this.filters.stateProvince = null
        this.filters.cityLga = null
        this.filters.facilityIds = []
        this.stateOptions = []
        this.cityOptions = []
        this.keywordOptions = []
        this.facilityOptions = []
        if (!this.filters.customer) await this.fetchAllCustomerOptions()
        return
      }
      // Narrow the customer list first (latest request wins) so a quick customer pick can't be overwritten.
      if (!this.filters.customer) this.fetchAllCustomerOptions(countryName)
      await this.selectCountry(countryName)
    },
    resetJourneyState () {
      this.filters.journey = null
      this.filters.conversationStageOption = null
      this.filters.readiness = []
      this.journeyOptions = []
      this.journeyStageMapping = []
      this.journeyConversations = []
    },
    async selectCountry (countryName) {
      if (!countryName) return
      this.appliedCountry = countryName
      this.resetJourneyState()
      if (this.keywordsCountry !== countryName) {
        // Keywords are per-country: a leftover one would keep filtering while no longer being visible.
        this.filters.entryKeywords = []
        this.keywordSearch = ''
        this.keywordsCountry = countryName
      }
      this.filters.country = countryName
      this.filters.country_id = this.countryIdMap[countryName] || null
      this.filters.stateProvince = null
      this.filters.cityLga = null
      this.filters.facilityIds = []
      this.filters.offeringIds = []
      this.filters.facilityScopeToCustomer = false
      this.markManual('country')
      this.facilityOptions = []
      await Promise.all([
        this.fetchJourneyOptionsFor(countryName, this.filters.customer),
        this.fetchStateOptionsFor(countryName),
        this.fetchKeywordOptionsFor(countryName),
        this.fetchOfferingOptionsFor(countryName)
      ])
    },
    clampFunnelRange () {
      const fromIdx = this.filters.funnelFrom === '' ? 0 : funnelStageValues.indexOf(this.filters.funnelFrom)
      const toIdx = this.filters.funnelTo === 'any' ? funnelStageValues.length : funnelStageValues.indexOf(this.filters.funnelTo)
      // "To" is exclusive, so it must stay strictly after "From"; otherwise fall back to "any".
      if (toIdx <= fromIdx) this.filters.funnelTo = 'any'
    },
    handleFunnelFromInput () {
      this.markManual('funnelFrom')
      this.clampFunnelRange()
      this.maybeGenerateName()
    },
    handleStateInput (stateName) {
      this.markManual('stateProvince')
      this.filters.cityLga = null
      this.fetchCityOptionsFor(this.filters.country, stateName)
    },
    handleJourneyInput () {
      this.markManual('journey')
      this.filters.conversationStageOption = null
      this.filters.readiness = []
      this.fetchJourneyStageMapping(this.filters.journey)
      this.fetchJourneyConversations(this.filters.journey)
      this.maybeGenerateName()
    },
    // Real per-journey funnel-stage config, saved via Journey Settings > Funnel Stage Mapping —
    // gates/sources the Readiness section (Conversation Stage no longer uses this, see below).
    async fetchJourneyStageMapping (journeyName) {
      this.journeyStageMapping = []
      if (!journeyName) return
      try {
        const response = await apiold.buildRequest('journey-settings', 'GET', { app: journeyName })
        const value = response.data.payload && response.data.payload.value
        this.journeyStageMapping = (value && value.funnel_stage_mapping) || []
      } catch (error) {
        this.journeyStageMapping = []
      }
    },
    // Journey's own raw conversation stages (same endpoint Journey Settings' Funnel Stage Mapping
    // uses to populate its "Choose stage..." picker) — backs the Conversation Stage field directly.
    async fetchJourneyConversations (journeyName) {
      this.journeyConversations = []
      if (!journeyName) return
      this.loadingJourneyConversations = true
      try {
        const response = await apiold.buildRequest('getJourneyConversationsList', 'GET', { app: journeyName })
        this.journeyConversations = response.data.payload || []
      } catch (error) {
        this.journeyConversations = []
      } finally {
        this.loadingJourneyConversations = false
      }
    },
    handleConversationStageInput () {
      this.markManual('conversationStageOption')
      this.maybeGenerateName()
    },
    maybeGenerateName () {
      if (this.nameManuallyEdited) return
      const existingNames = this.existingSegments.map(s => s.queryName)
      this.segmentName = generateSegmentName(this.filters.journey, this.filters.conversationStageOption, existingNames)
    },
    fuzzyMatch (options, text) {
      const needle = (text || '').toLowerCase().trim()
      if (!needle) return null
      return options.find(o => o.id.toLowerCase() === needle) ||
        options.find(o => o.id.toLowerCase().includes(needle) || needle.includes(o.id.toLowerCase())) ||
        null
    },
    async applyAiFilters (aiFilters) {
      // NlFilterBar already switched the full-page loader on (generating-start) for the AI call;
      // keep it on through option resolution + the count, then clear it here in finally.
      this.loading = true
      try {
        await this.doApplyAiFilters(aiFilters)
      } finally {
        this.loading = false
      }
    },
    // Snap a number to the closest offered option (null when nothing sensible exists).
    snapToOption (value, allowed) {
      const n = Number(value)
      if (!isFinite(n) || n <= 0) return null
      return allowed.reduce((best, cur) => (Math.abs(cur - n) < Math.abs(best - n) ? cur : best), allowed[0])
    },
    // Resolve the model's stage text ("welcome") to a real "conversation::stage" option id.
    resolveConversationStage (value) {
      const options = this.conversationStageOptionsForJourney
      const needle = String(value || '').toLowerCase().trim()
      if (!needle) return null
      const byId = options.find(o => String(o.id).toLowerCase() === needle)
      if (byId) return byId.id
      const stagePart = o => String(o.id).split('::').pop().toLowerCase()
      const hit = options.find(o => stagePart(o) === needle) || options.find(o => stagePart(o).includes(needle) || needle.includes(stagePart(o)))
      return hit ? hit.id : null
    },
    async doApplyAiFilters (aiFilters) {
      this.aiIgnored = []
      const ignore = (label, value) => this.aiIgnored.push(`I couldn't apply ${label} "${value}" because it isn't an available option here.`)
      const note = text => this.aiIgnored.push(text)
      if (aiFilters.customer) {
        this.filters.customer = aiFilters.customer
        await this.handleCustomerInput(aiFilters.customer)
        this.markAi('customer')
      }
      if (aiFilters.country) {
        // With a customer set, only accept a country that customer operates in (never a mismatched pair).
        const countryAllowed = !this.filters.customer || this.countryChoicesForCustomer.includes(aiFilters.country)
        if (countryAllowed) {
          // Country without a customer: narrow the customer dropdown, same as a manual country pick.
          if (!this.filters.customer) this.fetchAllCustomerOptions(aiFilters.country)
          await this.selectCountry(aiFilters.country)
          this.markAi('country')
        }
      }
      if (aiFilters.journey) {
        if (this.filters.journey !== aiFilters.journey) {
          // A different journey has different stages/readiness options: don't carry the old ones over.
          this.filters.conversationStageOption = null
          this.filters.readiness = []
          this.markManualMany(['conversationStageOption', 'readiness'])
        }
        this.filters.journey = aiFilters.journey
        this.markAi('journey')
        await Promise.all([
          this.fetchJourneyStageMapping(aiFilters.journey),
          this.fetchJourneyConversations(aiFilters.journey)
        ])
      }

      // A place name with no country: find the country from the data itself rather than relying on the
      // model to have guessed it (it doesn't every time).
      if (aiFilters.stateProvince && !this.filters.country) {
        try {
          const response = await apiold.buildRequest('/audience-builder/journey-map/find-location', 'GET', { name: aiFilters.stateProvince })
          const found = (response.data.payload || [])[0]
          if (found) {
            if (!this.filters.customer) this.fetchAllCustomerOptions(found.country)
            await this.selectCountry(found.country)
            this.markAi('country')
            aiFilters = { ...aiFilters, stateProvince: found.state }
            note(`I took "${found.state}" to be in ${found.country}. Change the country if that's wrong.`)
          }
        } catch (error) {
          // fall through to the "no country selected" message below
        }
      }
      if ((aiFilters.stateProvince || aiFilters.cityLga) && !this.filters.country) {
        note(`I couldn't apply the location "${aiFilters.stateProvince || aiFilters.cityLga}" because no country is selected. Choose a customer or country and try again.`)
      } else if (aiFilters.stateProvince) {
        const stateMatch = this.fuzzyMatch(this.stateOptions, aiFilters.stateProvince)
        if (!stateMatch) {
          note(`I couldn't find "${aiFilters.stateProvince}" among the states of ${this.filters.country}, so no location filter was applied.`)
        } else {
          this.filters.stateProvince = stateMatch.id
          this.markAi('stateProvince')
          await this.fetchCityOptionsFor(this.filters.country, stateMatch.id)
          if (aiFilters.cityLga) {
            const cityMatch = this.fuzzyMatch(this.cityOptions, aiFilters.cityLga)
            if (cityMatch) {
              this.filters.cityLga = cityMatch.id
              this.markAi('cityLga')
            } else {
              note(`I couldn't find the city "${aiFilters.cityLga}" in ${stateMatch.id}, so only the state was applied.`)
            }
          }
        }
      }

      // Only values that exist as options in the UI are applied; anything else would filter invisibly.
      const allowedFor = {
        channels: channelOptions.map(o => o.value),
        gender: genderOptions.map(o => o.value),
        barriers: barrierOptions.map(o => o.value),
        entryKeywords: this.keywordOptions
      }
      Object.keys(allowedFor).forEach(field => {
        if (!Array.isArray(aiFilters[field]) || !aiFilters[field].length) return
        const valid = aiFilters[field].filter(v => allowedFor[field].includes(v))
        aiFilters[field].filter(v => !valid.includes(v)).forEach(v => ignore(field, v))
        if (valid.length) {
          this.filters[field] = valid
          // Barriers are only "AI populated" once something real was applied.
          this.markAi(field)
        }
      })

      if (aiFilters.readiness) {
        if (this.readinessStageOptions.includes(aiFilters.readiness)) {
          this.filters.readiness = [aiFilters.readiness]
          this.markAi('readiness')
        } else {
          ignore('readiness', aiFilters.readiness)
        }
      }
      if (aiFilters.activeConversationExclude !== undefined && aiFilters.activeConversationExclude !== null) {
        this.filters.activeConversationExclude = !!aiFilters.activeConversationExclude
        this.markAi('activeConversationExclude')
      }

      // Preferred: explicit From/To range from the model (matches the funnel UI directly).
      if (aiFilters.funnelStageFrom && funnelStageValues.indexOf(aiFilters.funnelStageFrom) !== -1) {
        // funnelFrom stores '' for the first stage (reached_chatbot) — that's the select's value.
        this.filters.funnelFrom = aiFilters.funnelStageFrom === funnelStageValues[0] ? '' : aiFilters.funnelStageFrom
        this.markAi('funnelFrom')
      }
      if (aiFilters.funnelStageTo && funnelStageValues.indexOf(aiFilters.funnelStageTo) !== -1) {
        this.filters.funnelTo = aiFilters.funnelStageTo
        this.markAi('funnelTo')
      } else if ((aiFilters.funnelStageTo === null || aiFilters.funnelStageTo === undefined || aiFilters.funnelStageTo === '' || aiFilters.funnelStageTo === 'any') && aiFilters.funnelStageFrom) {
        // No upper bound given (or explicitly "any"): don't keep a stale, possibly conflicting one.
        this.filters.funnelTo = 'any'
      }
      // Backward-compat: older schema returned a flat funnelStages[] — collapse to a From/To range.
      if (Array.isArray(aiFilters.funnelStages) && aiFilters.funnelStages.length) {
        const indices = aiFilters.funnelStages.map(s => funnelStageValues.indexOf(s)).filter(i => i !== -1)
        if (indices.length) {
          const minIdx = Math.min(...indices)
          const maxIdx = Math.max(...indices)
          this.filters.funnelFrom = minIdx === 0 ? '' : funnelStageValues[minIdx]
          // "To" is exclusive — the stage after the furthest reached one (or "any" if it's the last).
          this.filters.funnelTo = maxIdx >= funnelStageValues.length - 1 ? 'any' : funnelStageValues[maxIdx + 1]
          this.markAi('funnelFrom')
        }
      }

      if (aiFilters.conversationStageOption) {
        const resolved = this.resolveConversationStage(aiFilters.conversationStageOption)
        if (resolved) {
          this.filters.conversationStageOption = resolved
          this.markAi('conversationStageOption')
        } else {
          ignore('conversation stage', aiFilters.conversationStageOption)
        }
      }
      ;['minAge', 'maxAge'].forEach(field => {
        const value = aiFilters[field]
        if (value === undefined || value === null || value === '') return
        // Clamp into the offered 15-65 range (e.g. "under 12" -> 15) instead of a blank select that still filters.
        const snapped = this.snapToOption(value, ageOptions.map(o => o.id).filter(id => typeof id === 'number'))
        if (snapped !== null) {
          this.filters[field] = snapped
          this.markAi(field)
        } else {
          ignore(field, value)
        }
      })

      const recencyDays = recencyOptions.map(o => o.id).filter(id => typeof id === 'number')
      if (aiFilters.recencyFromDays !== undefined && aiFilters.recencyFromDays !== null) {
        const snapped = this.snapToOption(aiFilters.recencyFromDays, recencyDays)
        if (snapped !== null) {
          this.filters.recencyFrom = snapped
          this.markAi('recencyFrom')
        } else {
          ignore('inactive from (days)', aiFilters.recencyFromDays)
        }
      }
      if (aiFilters.recencyToDays !== undefined && aiFilters.recencyToDays !== null) {
        const snapped = this.snapToOption(aiFilters.recencyToDays, recencyDays)
        if (snapped !== null) {
          this.filters.recencyTo = snapped
          this.markAi('recencyTo')
        } else {
          ignore('inactive to (days)', aiFilters.recencyToDays)
        }
      }

      // "Date entered <stage>" absolute range (distinct from recency/inactivity above).
      if (aiFilters.stageEnteredFrom) {
        this.filters.stageEnteredFrom = aiFilters.stageEnteredFrom
        this.markAi('stageEnteredFrom')
      }
      if (aiFilters.stageEnteredTo) {
        this.filters.stageEnteredTo = aiFilters.stageEnteredTo
        this.markAi('stageEnteredTo')
      }

      if (!this.filters.customer) note('Choose a customer to see the audience size, the count needs one.')
      this.clampFunnelRange()
      this.maybeGenerateName()
      // Awaited so the full-page loader (managed by applyAiFilters' finally) stays up until the
      // count returns, instead of clearing the moment the fetch is fired.
      if (this.filters.country_id) await this.getAudience()
    },
    async loadSegmentForEdit (id) {
      try {
        const response = await api.get(`audience-builder/${this.env.env}/queries`, { params: { id }, baseURL: this.env.baseURL })
        const data = response.data.data[0]
        this.filters = { ...defaultFilters(), ...criteriaToFilters(data.criteria || {}), id: data.id }
        this.appliedCustomer = this.filters.customer || null
        this.appliedCountry = this.filters.country || null
        this.keywordsCountry = this.filters.country || null
        this.segmentName = data.query_name || ''
        this.nameManuallyEdited = true
        // Show the segment's saved count right away (no re-query — the live count can be slow); the
        // user can hit "Get Audience" to recompute against current data.
        if (data.criteria && data.criteria.savedUserCount !== undefined && data.criteria.savedUserCount !== null) {
          this.audienceCount = data.criteria.savedUserCount
          this.countRun = true
        }
        if (this.filters.customer) {
          try {
            const response2 = await apiold.buildRequest('/audience-builder/journey-map/countries-for-customer', 'GET', { customer: this.filters.customer })
            this.countryChoicesForCustomer = response2.data.payload || []
          } catch (error) {
            this.countryChoicesForCustomer = []
          }
        }
        if (this.filters.country) {
          await Promise.all([
            this.fetchJourneyOptionsFor(this.filters.country, this.filters.customer),
            this.fetchStateOptionsFor(this.filters.country),
            this.fetchKeywordOptionsFor(this.filters.country),
            this.fetchOfferingOptionsFor(this.filters.country),
            this.hydrateSavedFacility(this.filters.country, this.filters.facilityIds),
            this.fetchJourneyStageMapping(this.filters.journey),
            this.fetchJourneyConversations(this.filters.journey)
          ])
          if (this.filters.stateProvince) await this.fetchCityOptionsFor(this.filters.country, this.filters.stateProvince)
        }
      } catch (error) {
        this.$swal({ type: 'error', text: 'Failed to load segment for editing' })
      } finally {
        // Loading replaces every filter at once; that isn't a user edit, so don't flag the count stale.
        this.$nextTick(() => { this.countStale = false })
      }
    },
    formatAudienceRawData (rawData) {
      return (rawData || []).map((ele, index) => ({
        key: index,
        accountId: ele.accountId,
        country: ele.country || '-',
        channel: ele.usertype || '-',
        gender: ele.gender || '-',
        age: ele.age || '-',
        language: ele.language || '-',
        sessionId: ele.sessionId,
        keyword: ele.keyword,
        conversation: ele.conversation,
        stage: ele.stage,
        answer: ele.answer,
        queText: ele.queText,
        createdDate: ele.engagementCreatedAt ? moment(ele.engagementCreatedAt).utc().format('DD-MMM-YYYY, hh:mm A') : '-'
      }))
    },
    hasValidationErrors () {
      const v = validateFilters(this.filters)
      return !v.ageValid || !v.recencyValid || !v.funnelRangeValid || !v.stageEnteredValid || !v.proximityRadiusValid
    },
    // Returns true when a fresh count was computed for the current filters.
    async getAudience () {
      // Surface inline validation messages (e.g. required radius) instead of failing silently.
      if (this.hasValidationErrors()) {
        this.submitted = true
        return false
      }
      if (!this.filters.customer || !this.filters.country_id) {
        this.submitted = true
        return false
      }
      this.loading = true
      const payload = buildCountPayload(this.filters, this.filters.country)
      try {
        const response = await api.post(`audience-builder/${this.env.env}/users/re-engagement-audience`, payload, { baseURL: this.env.baseURL })
        this.audienceCount = Number(response.data.totalCounts) || 0
        this.audienceData = this.formatAudienceRawData(response.data.data)
        this.countRun = true
        this.countStale = false
        return true
      } catch (error) {
        // Never leave the previous filters' numbers on screen as if they were this result.
        this.audienceCount = null
        this.audienceData = []
        this.countRun = false
        this.countStale = false
        if (error.response && error.response.status === 404) {
          // The statistics service answers 404 when nothing matches: that is a genuine zero.
          this.audienceCount = 0
          this.countRun = true
          return true
        }
        this.$swal({ type: 'error', text: error.response && error.response.data && error.response.data.message ? error.response.data.message : 'An error occurred' })
        return false
      } finally {
        this.loading = false
      }
    },
    async saveSegment () {
      this.submitted = true
      if (!this.filters.customer || !this.filters.country_id || !this.filters.journey || !this.filters.channels.length || !this.segmentName.trim()) return
      if (this.hasValidationErrors()) return

      // Always store a count computed from the filters being saved (never 0 or a stale number).
      if (!(await this.getAudience())) return

      this.loading = true
      const countPayload = buildCountPayload(this.filters, this.filters.country)
      const criteria = buildSaveCriteria(this.filters, countPayload, this.audienceCount)
      const params = {
        query_name: this.segmentName.trim(),
        country_id: this.filters.country_id,
        country_name: this.filters.country,
        criteria,
        query_type: 'AI'
      }
      // Only send id when editing — passing id:null spreads an explicit NULL into the insert's
      // serial PK and trips a NOT NULL violation (the working create path omits id entirely).
      if (this.filters.id) params.id = this.filters.id
      try {
        await api.post(`audience-builder/${this.env.env}/queries/save`, params, { baseURL: this.env.baseURL })
        this.$emit('segment-saved')
        await this.fetchExistingSegments()
        if (!this.filters.id) {
          // Created: later saves must update this segment rather than collide on its name.
          const saved = this.existingSegments.find(seg => seg.queryName === params.query_name)
          if (saved) this.filters.id = saved.id
          this.nameManuallyEdited = true
        }
        this.$swal({ type: 'success', text: 'Segment saved successfully' })
      } catch (error) {
        if (error.response && error.response.status === 409) {
          this.$swal({ type: 'error', text: 'A segment with the same name already exists' })
        } else {
          this.$swal({ type: 'error', text: error.response && error.response.data ? error.response.data.message : 'An error occurred' })
        }
      } finally {
        this.loading = false
      }
    },
    clearFilters () {
      const preserved = { id: this.filters.id, country: this.filters.country, country_id: this.filters.country_id, customer: this.filters.customer, journey: this.filters.journey }
      this.filters = { ...defaultFilters(), ...preserved }
      this.aiPopulated = {}
      this.aiIgnored = []
      this.submitted = false
      // Cleared filters invalidate the old count and sample rows.
      this.audienceCount = null
      this.audienceData = []
      this.countRun = false
      this.countStale = false
      // Editing a saved segment: keep its name, otherwise Update would silently rename it.
      if (!this.filters.id) {
        this.nameManuallyEdited = false
        this.maybeGenerateName()
      }
    }
  }
}
</script>

<style scoped>
.required:after {
  content: ' *';
  color: red;
}
.text-red {
  display: block;
  color: #c0392b;
  font-size: 12px;
  margin-top: 3px;
}
.text-muted {
  color: #999;
}

/* Customer bar */
.cust-bar {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: start;
  gap: 12px 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 16px;
  margin-bottom: 16px;
}
.cust-field {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  min-width: 0;
}
.cust-meta {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.cust-label {
  font-size: 13px;
  font-weight: 500;
  color: #4a5568;
  white-space: nowrap;
}
.cust-select {
  width: 100%;
  min-width: 0;
}
.cust-context-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #E6F5F4;
  border: 1px solid rgba(13, 148, 136, 0.25);
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  color: #0B7C72;
  font-weight: 500;
  white-space: nowrap;
}
.cust-hint {
  font-size: 12px;
  color: #9e9e9e;
}

/* 2-column layout */
.builder-layout {
  display: grid;
  grid-template-columns: 1fr 330px;
  gap: 20px;
  align-items: start;
}
.builder-right {
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
@media (max-width: 900px) {
  .builder-layout {
    grid-template-columns: 1fr;
  }
  .builder-right {
    position: static;
  }
}

/* Filter panel */
.filter-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
.fp-hdr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #f9fafb;
  border-radius: 10px 10px 0 0;
  flex-wrap: wrap;
  gap: 8px;
}
.fp-title {
  font-size: 14px;
  font-weight: 700;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 8px;
}
.ai-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}
.ai-badge-ai {
  background: linear-gradient(135deg, #0D9488, #1565c0);
  color: #fff;
}
.ai-badge-manual {
  background: #edf2f7;
  color: #4a5568;
}
.fp-hdr-actions {
  display: flex;
  gap: 14px;
}
.fp-action {
  font-size: 12px;
  color: #718096;
  text-decoration: none;
}
.fp-action:hover {
  text-decoration: underline;
}
.fp-action-danger {
  color: #c0392b;
}
.fp-body {
  padding: 12px 16px 16px;
}

/* Collapsible sections — each one is its own shaded card so sections read as distinct blocks */
.filter-section {
  background: #f7f8fa;
  border: 1px solid #e9ebee;
  border-radius: 8px;
  margin-bottom: 10px;
  padding: 0 14px;
  transition: box-shadow 0.25s, background 0.25s;
}
.filter-section:last-child {
  margin-bottom: 0;
}
.fs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 0;
  cursor: pointer;
  user-select: none;
}
.fs-title-text {
  font-size: 11px;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.fs-arrow {
  font-size: 11px;
  color: #9e9e9e;
  transition: transform 0.2s;
}
.filter-section.collapsed .fs-arrow {
  transform: rotate(-90deg);
}
.fs-body {
  padding-bottom: 14px;
}
@keyframes aiFlash {
  0% {
    box-shadow: inset 3px 0 0 0 #0D9488;
    background: rgba(13, 148, 136, 0.08);
  }
  100% {
    box-shadow: inset 3px 0 0 0 transparent;
    background: #f7f8fa;
  }
}
.filter-section.ai-updated {
  animation: aiFlash 2.5s ease forwards 0.3s;
}

/* Rows / groups */
.filter-row {
  display: grid;
  gap: 12px;
  margin-bottom: 10px;
}
.filter-row.cols-2 {
  grid-template-columns: 1fr 1fr;
}
.fg {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.fg-lbl {
  font-size: 11px;
  font-weight: 600;
  color: #4a5568;
  letter-spacing: 0.02em;
}
.fg-lbl .req {
  color: #c0392b;
  font-size: 9px;
  margin-left: 2px;
}
.fg-sub {
  font-size: 11px;
  color: #9e9e9e;
  font-weight: 400;
  margin-left: 4px;
}
.fg-sub-note {
  font-size: 11px;
  color: #9e9e9e;
  margin-top: 4px;
}

/* Native selects/inputs (kept plain to match the mockup's fixed-list controls) */
.form-select,
.form-input {
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 13px;
  color: #212121;
  outline: none;
  background: #fff;
  width: 100%;
}
.form-select:focus,
.form-input:focus {
  border-color: #0D9488;
}

/* Chips */
.chip-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}
.fchip {
  padding: 5px 11px;
  border-radius: 12px;
  border: 1.5px solid #bdbdbd;
  color: #616161;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s;
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
}
.fchip:hover {
  border-color: #0D9488;
  color: #0B7C72;
}
.fchip.sel {
  border-color: #0D9488;
  background: #E6F5F4;
  color: #0B7C72;
}
.fchip .chk {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  border: 1.5px solid currentColor;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  flex-shrink: 0;
}
.fchip.sel .chk::after {
  content: '✓';
}

/* Ranges */
.date-range-row,
.funnel-range-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.date-range-row .form-select,
.date-range-row .form-input {
  flex: 1;
  min-width: 120px;
}
.range-sep {
  font-size: 12px;
  color: #9e9e9e;
  white-space: nowrap;
}
.funnel-range-col {
  flex: 1;
  min-width: 160px;
}
.funnel-range-label {
  font-size: 10.5px;
  color: #9e9e9e;
  margin-bottom: 4px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.funnel-range-sep {
  font-size: 18px;
  color: #9e9e9e;
  padding-top: 18px;
}
.funnel-hint {
  font-size: 11.5px;
  color: #0B7C72;
  background: #E6F5F4;
  border-radius: 4px;
  padding: 6px 9px;
  margin-top: 8px;
  line-height: 1.5;
}
.funnel-from-label {
  color: #0B7C72;
  font-weight: 600;
}

.not-enforced-note {
  display: block;
  color: #c53030;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  margin-top: 6px;
}
.warn-note {
  font-size: 11px;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 4px;
  padding: 6px 9px;
  margin-top: 8px;
  display: flex;
  gap: 6px;
  align-items: flex-start;
}
.barriers-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  background: #fafafa;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 12px;
  color: #9e9e9e;
}
.barriers-row a {
  color: #0B7C72;
  text-decoration: none;
  font-weight: 500;
  white-space: nowrap;
}

.exclusion-checklist {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #eee;
  padding: 8px;
  border-radius: 4px;
}
.exclusion-row {
  padding: 2px 0;
  font-size: 13px;
}
.exclusion-count {
  color: #999;
  font-size: 12px;
}
.active-conv-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 9px 12px;
  background: #fafafa;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 13px;
  color: #616161;
  transition: background 0.12s, border-color 0.12s;
}
.active-conv-toggle-on {
  background: #E6F5F4;
  border-color: #0D9488;
}
.active-conv-check {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1.5px solid #bdbdbd;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #fff;
  flex-shrink: 0;
}
.active-conv-toggle-on .active-conv-check {
  background: #0D9488;
  border-color: #0D9488;
}

/* Right panel */
.aud-card {
  background: #fff;
  border: 1.5px solid #a5d6a7;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.aud-count-box {
  background: #e8f5e9;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.aud-num {
  font-size: 40px;
  font-weight: 700;
  color: #2e7d32;
  font-family: 'DM Mono', monospace;
  line-height: 1;
}
.aud-lbl {
  font-size: 12px;
  color: #2e7d32;
  font-weight: 500;
  line-height: 1.6;
}
.pill {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}
.pill-green {
  background: #e8f5e9;
  color: #2e7d32;
}
.aud-note {
  padding: 10px 16px 0;
  font-size: 11.5px;
  color: #718096;
  line-height: 1.5;
}
.aud-card {
  padding-bottom: 14px;
}
.save-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}
.save-card-title {
  font-size: 11px;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 10px;
}
.seg-name-input {
  border: 1.5px solid #bdbdbd;
  border-radius: 4px;
  padding: 7px 10px;
  font-size: 13px;
  color: #212121;
  outline: none;
  width: 100%;
  display: block;
  margin-bottom: 6px;
}
.seg-name-input:focus {
  border-color: #e8305a;
}
.seg-name-auto {
  font-size: 11px;
  color: #9e9e9e;
  display: block;
  margin-bottom: 10px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.15s;
}
.aud-card .btn-block {
  display: block;
  width: calc(100% - 32px);
  margin: 14px 16px 0;
}
.save-card .btn-block {
  display: block;
  width: 100%;
  margin-top: 4px;
}
.btn-pink {
  background: #e8305a;
  color: #fff;
}
.btn-pink:hover {
  background: #c9284e;
}
.btn-outline {
  background: #fff;
  color: #616161;
  border: 1px solid #bdbdbd;
}
.btn-outline:hover {
  background: #f5f5f5;
}
</style>



// WEBPACK FOOTER //
// form.vue?5eae90b0